/* ═══════════════════════════════════════════════════════════════
   The Designer — find & replace.

   A spec-domain ProseMirror plugin (the kind the contract allows):
   ProseMirror still owns the document, the selection and the undo
   history — this plugin only derives WHERE the query matches and
   paints those ranges as decorations. Replacing goes through normal
   document transactions, so a Replace All is ONE transaction and
   therefore ONE Ctrl+Z step.

   The UI lives in FindReplace.tsx; the keymap entry (Ctrl/Cmd+F)
   lives with the other spec shortcuts in commands.ts.
   ═══════════════════════════════════════════════════════════════ */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';

export interface SearchMatch {
  from: number;
  to: number;
}

export interface FindState {
  open: boolean;
  query: string;
  /** Which match prev/next is parked on. Always < matches.length when any. */
  activeIndex: number;
  matches: SearchMatch[];
}

type PluginState = FindState & { decorations: DecorationSet };

type FindAction =
  | { type: 'open'; query?: string }
  | { type: 'close' }
  | { type: 'query'; query: string }
  | { type: 'step'; dir: 1 | -1 };

export const searchPluginKey = new PluginKey<PluginState>('designerSearch');

/**
 * Every place the query occurs in the document's prose, in document order.
 * Case-insensitive, non-overlapping — Word's behaviour. Matching runs on each
 * paragraph's full text, so a hit that spans a bold/italic boundary (several
 * text nodes, one paragraph) is still one match. Matches never cross
 * paragraphs: the query box is one line, so the needle never contains one.
 */
export function findMatches(doc: PMNode, query: string): SearchMatch[] {
  const q = query.toLowerCase();
  if (!q) return [];
  const out: SearchMatch[] = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    const text = node.textContent.toLowerCase();
    let i = text.indexOf(q);
    while (i !== -1) {
      out.push({ from: pos + 1 + i, to: pos + 1 + i + q.length });
      i = text.indexOf(q, i + q.length);
    }
    return false;
  });
  return out;
}

/** The first match at or after the caret — where Word starts a search. */
function nearestIndex(matches: SearchMatch[], from: number): number {
  const i = matches.findIndex((m) => m.from >= from);
  return i === -1 ? 0 : i;
}

function buildDecorations(doc: PMNode, matches: SearchMatch[], activeIndex: number): DecorationSet {
  if (!matches.length) return DecorationSet.empty;
  return DecorationSet.create(
    doc,
    matches.map((m, i) =>
      Decoration.inline(m.from, m.to, {
        class: i === activeIndex ? 'dsg-find-hit dsg-find-active' : 'dsg-find-hit',
      })
    )
  );
}

export function createSearchPlugin(): Plugin<PluginState> {
  return new Plugin<PluginState>({
    key: searchPluginKey,
    state: {
      init: () => ({
        open: false,
        query: '',
        activeIndex: 0,
        matches: [],
        decorations: DecorationSet.empty,
      }),
      apply: (tr: Transaction, prev: PluginState, _old, newState): PluginState => {
        const action = tr.getMeta(searchPluginKey) as FindAction | undefined;
        let next: FindState = prev;

        if (action) {
          switch (action.type) {
            case 'open': {
              const query = action.query ?? prev.query;
              const matches = findMatches(newState.doc, query);
              next = {
                open: true,
                query,
                matches,
                activeIndex: nearestIndex(matches, newState.selection.from),
              };
              break;
            }
            case 'close':
              // The query survives a close so reopening resumes the search.
              next = { open: false, query: prev.query, matches: [], activeIndex: 0 };
              break;
            case 'query': {
              const matches = findMatches(newState.doc, action.query);
              next = {
                open: true,
                query: action.query,
                matches,
                activeIndex: nearestIndex(matches, newState.selection.from),
              };
              break;
            }
            case 'step': {
              const n = prev.matches.length;
              if (n) next = { ...prev, activeIndex: (prev.activeIndex + action.dir + n) % n };
              break;
            }
          }
        } else if (tr.docChanged && prev.open && prev.query) {
          // The document changed under an open search (typing, a replace,
          // an undo) — re-derive. After a replace, the SAME index now points
          // at the next remaining match, which is exactly Word's cadence.
          const matches = findMatches(newState.doc, prev.query);
          next = {
            ...prev,
            matches,
            activeIndex: matches.length ? Math.min(prev.activeIndex, matches.length - 1) : 0,
          };
        } else if (!tr.docChanged) {
          return prev;
        }

        return {
          ...next,
          decorations: buildDecorations(
            newState.doc,
            next.open ? next.matches : [],
            next.activeIndex
          ),
        };
      },
    },
    props: {
      decorations(state) {
        return searchPluginKey.getState(state)?.decorations ?? DecorationSet.empty;
      },
    },
  });
}

/** The plugin, packaged for the editor's extension list. */
export const DesignerSearch = Extension.create({
  name: 'designerSearch',
  addProseMirrorPlugins() {
    return [createSearchPlugin()];
  },
});

// ─── Commands ───

export function getFindState(state: EditorState): FindState | null {
  const s = searchPluginKey.getState(state);
  if (!s) return null;
  const { open, query, activeIndex, matches } = s;
  return { open, query, activeIndex, matches };
}

type Dispatch = ((tr: Transaction) => void) | undefined;

function send(state: EditorState, dispatch: Dispatch, action: FindAction): boolean {
  if (dispatch) dispatch(state.tr.setMeta(searchPluginKey, action));
  return true;
}

export function openFind(state: EditorState, dispatch: Dispatch, query?: string): boolean {
  return send(state, dispatch, { type: 'open', query });
}

export function closeFind(state: EditorState, dispatch: Dispatch): boolean {
  return send(state, dispatch, { type: 'close' });
}

export function setFindQuery(state: EditorState, dispatch: Dispatch, query: string): boolean {
  return send(state, dispatch, { type: 'query', query });
}

export function stepFind(state: EditorState, dispatch: Dispatch, dir: 1 | -1): boolean {
  return send(state, dispatch, { type: 'step', dir });
}

/** The emphasis the replaced text carried, so a replacement inside a bold
 *  run stays bold — Word's behaviour. */
function marksAt(state: EditorState, pos: number) {
  const $pos = state.doc.resolve(pos);
  return $pos.nodeAfter?.marks ?? $pos.marks();
}

/** Replace the active match. One transaction; recount happens in apply(). */
export function replaceActive(
  state: EditorState,
  dispatch: Dispatch,
  replacement: string
): boolean {
  const s = searchPluginKey.getState(state);
  if (!s?.open || !s.matches.length) return false;
  const m = s.matches[Math.min(s.activeIndex, s.matches.length - 1)];
  const tr = state.tr;
  if (replacement)
    tr.replaceWith(m.from, m.to, state.schema.text(replacement, marksAt(state, m.from)));
  else tr.delete(m.from, m.to);
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/**
 * Replace every match in ONE transaction, so one Ctrl+Z restores the whole
 * document. Matches are rewritten back-to-front: every position earlier in
 * the document is untouched by the replacements after it, so no mapping
 * arithmetic is needed — or can drift.
 */
export function replaceAllMatches(
  state: EditorState,
  dispatch: Dispatch,
  replacement: string
): boolean {
  const s = searchPluginKey.getState(state);
  if (!s?.open || !s.matches.length) return false;
  const tr = state.tr;
  for (let i = s.matches.length - 1; i >= 0; i--) {
    const m = s.matches[i];
    if (replacement)
      tr.replaceWith(m.from, m.to, state.schema.text(replacement, marksAt(state, m.from)));
    else tr.delete(m.from, m.to);
  }
  if (dispatch) dispatch(tr);
  return true;
}
